import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Tabs, Space, Empty, SpinLoading } from 'antd-mobile';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import './MobileDoublesPartnerStats.css';

const MobileDoublesPartnerStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('all');
  const [activeTab, setActiveTab] = useState('frequent');
  const [playerName, setPlayerName] = useState('');
  const [ratingHistory, setRatingHistory] = useState([]);

  // 饼图颜色
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6E76', '#A0522D'];

  useEffect(() => {
    fetchPartnerStats();
    fetchRatingHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, timeRange]);

  const fetchPartnerStats = async () => {
    try {
      setLoading(true);
      // 获取球员信息
      const playerResponse = await axios.get(`/api/players/${id}`);
      if (playerResponse.data.success) {
        setPlayerName(playerResponse.data.data.name);
      }
      
      // 获取搭档统计
      const response = await axios.get(`/api/players/${id}/doubles-partners?timeRange=${timeRange}`);
      setStats(response.data.data);
    } catch (error) {
      console.error('获取双打搭档统计失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRatingHistory = async () => {
    try {
      const response = await axios.get(`/api/players/${id}/rating-history`);
      if (response.data.success) {
        setRatingHistory(response.data.data.doubles || []);
      }
    } catch (error) {
      console.error('获取积分历史失败:', error);
    }
  };

  const renderPartnerItem = (partner) => (
    <List.Item
      key={partner.partnerId}
      prefix={
        <Space direction="vertical" size="small">
          <span className="partner-name">{partner.partnerName}</span>
          <Space>
            <Tag color="success">{partner.winRate}% 胜率</Tag>
            <Tag color="primary">{partner.matchCount} 场</Tag>
          </Space>
        </Space>
      }
      description={
        <Space direction="vertical" size="small">
          <span>胜场: {partner.winCount} | 负场: {partner.lossCount}</span>
          <span>最近搭档: {new Date(partner.lastPlayedDate).toLocaleDateString()}</span>
        </Space>
      }
    />
  );

  const renderSummary = () => (
    <Card className="summary-card">
      <Space direction="vertical" size="small">
        <div className="summary-title">{playerName}的双打总体统计</div>
        <Space wrap>
          <Tag color="primary">总场次: {stats.totalDoublesMatches}</Tag>
          <Tag color="success">胜场: {stats.totalDoublesWins}</Tag>
          <Tag color="danger">负场: {stats.totalDoublesLosses}</Tag>
          <Tag color="warning">胜率: {stats.overallWinRate}%</Tag>
        </Space>
      </Space>
    </Card>
  );

  const renderRatingChart = () => {
    if (!ratingHistory || ratingHistory.length === 0) return null;

    // 处理数据
    const chartData = ratingHistory.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      rating: item.rating,
      ratingChange: item.ratingChange,
      eventName: item.eventName,
    }));

    return (
      <Card className="rating-chart-card">
        <div className="rating-chart-title">双打积分走势</div>
        <div className="rating-chart-container">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis domain={['dataMin - 50', 'dataMax + 50']} />
              <Tooltip 
                formatter={(value, name) => [value, name === 'rating' ? '积分' : '']}
                labelFormatter={label => `日期: ${label}`}
              />
              <Line 
                type="monotone" 
                dataKey="rating" 
                stroke="#1677ff" 
                strokeWidth={2}
                dot={{ r: 4, fill: '#1677ff' }} 
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        <div className="rating-events">
          <div className="rating-events-title">积分历史记录</div>
          <div className="rating-events-list">
            {chartData.slice().reverse().map((event, index) => (
              <div key={index} className="rating-event-item">
                <div className="rating-event-date">{event.date}</div>
                <div className="rating-event-details">
                  <span className="rating-event-name">{event.eventName || '比赛活动'}</span>
                  <span className={`rating-event-change ${event.ratingChange >= 0 ? 'positive' : 'negative'}`}>
                    {event.ratingChange >= 0 ? '+' : ''}{event.ratingChange}
                  </span>
                  <span className="rating-event-value">{event.rating}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  const renderPieChart = () => {
    if (!stats || !stats.mostFrequentPartners.length) return null;
    
    // 准备饼图数据
    const pieData = stats.mostFrequentPartners.map(partner => ({
      name: partner.partnerName,
      value: partner.matchCount,
      winRate: partner.winRate,
      wins: partner.winCount,
      losses: partner.lossCount
    }));

    return (
      <Card className="pie-chart-card">
        <div className="pie-chart-title">搭档比例分布</div>
        <div className="pie-chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name }) => `${name}`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `场次: ${value}`, 
                  `${props.payload.name}`
                ]}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="custom-tooltip">
                        <p className="tooltip-name">{data.name}</p>
                        <p className="tooltip-item">场次: {data.value}</p>
                        <p className="tooltip-item">胜率: {data.winRate}%</p>
                        <p className="tooltip-item">胜场: {data.wins} | 负场: {data.losses}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend 
                layout="vertical" 
                verticalAlign="middle" 
                align="right"
                formatter={(value, entry, index) => {
                  const data = pieData[index];
                  return (
                    <span className="legend-item">
                      {data.name} ({data.value}场, {data.winRate}%)
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="pie-chart-stats">
          <div className="stats-title">搭档详情</div>
          <div className="stats-list">
            {pieData.map((partner, index) => (
              <div key={index} className="stats-item" style={{ borderLeft: `4px solid ${COLORS[index % COLORS.length]}` }}>
                <div className="stats-name">{partner.name}</div>
                <div className="stats-details">
                  <Tag color="primary">场次: {partner.value}</Tag>
                  <Tag color={partner.winRate >= 50 ? "success" : "danger"}>胜率: {partner.winRate}%</Tag>
                  <div className="stats-win-loss">
                    <span className="stats-win">胜: {partner.wins}</span>
                    <span className="stats-loss">负: {partner.losses}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <SpinLoading />
        </div>
      );
    }

    if (!stats || (!stats.mostFrequentPartners.length && !stats.bestPartners.length && !stats.worstPartners.length)) {
      return (
        <Empty
          description="暂无双打搭档数据"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <>
        {renderSummary()}
        {renderRatingChart()}
        {renderPieChart()}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="partner-tabs"
        >
          <Tabs.Tab title="最常搭档" key="frequent">
            <List header="按搭档次数排序">
              {stats.mostFrequentPartners.map(renderPartnerItem)}
            </List>
          </Tabs.Tab>
          <Tabs.Tab title="最佳搭档" key="best">
            <List header="按胜率排序">
              {stats.bestPartners.map(renderPartnerItem)}
            </List>
          </Tabs.Tab>
          <Tabs.Tab title="最差搭档" key="worst">
            <List header="按负率排序">
              {stats.worstPartners.map(renderPartnerItem)}
            </List>
          </Tabs.Tab>
        </Tabs>
      </>
    );
  };

  return (
    <div className="doubles-partner-stats">
      <div className="page-header">
        <div className="back-button" onClick={() => navigate(-1)}>返回</div>
        <div className="page-title">{playerName}的双打搭档统计</div>
      </div>
      
      <div className="time-range-selector">
        <Space>
          <Tag
            color={timeRange === 'all' ? 'primary' : 'default'}
            onClick={() => setTimeRange('all')}
          >
            全部
          </Tag>
          <Tag
            color={timeRange === 'month' ? 'primary' : 'default'}
            onClick={() => setTimeRange('month')}
          >
            最近一月
          </Tag>
          <Tag
            color={timeRange === 'threeMonths' ? 'primary' : 'default'}
            onClick={() => setTimeRange('threeMonths')}
          >
            最近三月
          </Tag>
        </Space>
      </div>
      {renderContent()}
    </div>
  );
};

export default MobileDoublesPartnerStats; 